import { GetListParams, GetOneParams } from 'react-admin';
import { CustomDataProvider } from '../../providers/CustomDataProvider';
import { baseUrl, httpClient } from '../../providers/AppDataProvider';
import { getAuth } from '../../providers/AppAuthProvider';
import axios from 'axios';

export class HotSearchDataProvider implements CustomDataProvider {
  // 辅助函数：确保所有字段都被包含在数据中
  // eslint-disable-next-line class-methods-use-this
  private ensureAllFields(data: any): any {
    // 使用 ?? 和 || 来确保 null、undefined 和空字符串都被正确处理
    return {
      board: data?.board ?? '',
      mode: data?.mode ?? '',
      status: data?.status ?? 'ACTIVE',
      showFlame: data?.showFlame ?? false,
      displayText: data?.displayText ?? '',
      symbol: data?.symbol ?? '',
      chainId: data?.chainId ?? '',
      tokenContract: data?.tokenContract ?? '',
      tokenName: data?.tokenName ?? '',
      operator: data?.operator ?? '',
      // 保留 id（如果有的话，用于 update）
      ...(data?.id ? { id: data.id } : {}),
    };
  }

  // eslint-disable-next-line class-methods-use-this
  async getList(resource: string, params: GetListParams): Promise<any> {
    if (resource !== 'hotSearching')
      return Promise.reject(new Error('Unsupported getList resource'));

    const {
      filter = {
        board: '',
      },
    } = params;
    const board = filter?.board || 'CONTRACT';
    const status = filter?.status;

    const { page = 1, perPage = 10 } = params.pagination || {};

    try {
      // 构建查询参数
      const queryParams = new URLSearchParams();
      queryParams.append('board', board);
      if (status) {
        queryParams.append('status', status);
      }

      const url = `${baseUrl}/hotSearching/configs?${queryParams.toString()}`;
      const { json } = await httpClient(url, {
        method: 'GET',
      });

      const rows: any[] = json?.data ?? [];

      // 排序：ACTIVE 在最上面
      const sortedRows = rows.sort((a, b) => {
        if (a.status === 'ACTIVE' && b.status !== 'ACTIVE') return -1;
        if (a.status !== 'ACTIVE' && b.status === 'ACTIVE') return 1;
        return 0;
      });

      const total = sortedRows.length;

      // 确保每条记录都有 id
      const allData = sortedRows.map((r: any, i: number) => {
        const row = r?.id != null ? r : { id: `${i}`, ...r };
        return row;
      });

      // 前端分页处理
      const startIndex = (page - 1) * perPage;
      const endIndex = startIndex + perPage;
      const data = allData.slice(startIndex, endIndex);
      return { data, total };
    } catch (error) {
      console.error('HotSearchDataProvider getList error:', error);
      throw error;
    }
  }
  // eslint-disable-next-line class-methods-use-this
  async getOne(resource: string, params: GetOneParams): Promise<any> {
    if (resource !== 'hotSearching')
      return Promise.reject(new Error('Unsupported getOne resource'));

    try {
      // 构建查询参数
      const queryParams = new URLSearchParams();
      queryParams.append('board', 'ALL');
      const url = `${baseUrl}/hotSearching/configs?${queryParams.toString()}`;
      const { json } = await httpClient(url, {
        method: 'GET',
      });

      const rows: any[] = json?.data ?? [];

      // 确保每条记录都有 id
      const allData = rows.map((r: any, i: number) => {
        const row = r?.id != null ? r : { id: `${i}`, ...r };
        return row;
      });
      // console.log('params', params);
      // eslint-disable-next-line no-restricted-syntax,no-plusplus
      for (let i = 0; i < allData.length; i++) {
        if (allData[i].id === params.id) {
          return { data: { resource: resource, ...allData[i] } };
        }
      }

      return { data: { resource: resource, ...params } };
    } catch (error) {
      console.error('getOne error:', error);
      throw error;
    }
  }

  // eslint-disable-next-line class-methods-use-this
  async create(resource: string, params: any): Promise<any> {
    if (resource !== 'hotSearching')
      return Promise.reject(new Error('Unsupported create resource'));

    const url = `${baseUrl}/hotSearching/configs`;
    const data = params?.data ?? {};
    console.log('HotSearchDataProvider create data:', data);

    // 确保所有字段都被包含，即使它们是 disabled 或空值
    const payload = this.ensureAllFields(data);

    console.log('HotSearchDataProvider create payload:', payload);
    try {
      const { json } = await httpClient(url, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      if (json.data === null && json.errors?.length > 0) {
        throw new Error(json.errors[0].message);
      }
      return { data: json?.data ?? json };
    } catch (error) {
      console.error('HotSearchDataProvider create error:', error);
      throw error;
    }
  }

  // eslint-disable-next-line class-methods-use-this
  async update(resource: string, params: any): Promise<any> {
    if (resource !== 'hotSearching')
      return Promise.reject(new Error('Unsupported update resource'));

    const url = `${baseUrl}/hotSearching/configs`;
    const data = params?.data ?? {};
    console.log('HotSearchDataProvider update data:', data);

    // 确保所有字段都被包含，即使它们是 disabled 或空值
    const payload = this.ensureAllFields(data);

    console.log('HotSearchDataProvider update payload:', payload);
    try {
      const { json } = await httpClient(url, {
        method: 'POST',
        body: JSON.stringify(payload),
      });

      return { data: json?.data ?? json };
    } catch (error) {
      console.error('HotSearchDataProvider update error:', error);
      throw error;
    }
  }

  // eslint-disable-next-line class-methods-use-this
  async delete(resource: string, params: any): Promise<any> {
    if (resource !== 'hotSearching')
      return Promise.reject(new Error('Unsupported delete resource'));

    const url = `${baseUrl}/hotSearching/configs`;
    const payload = { ...params?.data, status: 'INACTIVE' };

    try {
      const { json } = await httpClient(url, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });

      return { data: json ?? { id: payload.id } };
    } catch (error) {
      console.error('HotSearchDataProvider delete error:', error);
      throw error;
    }
  }

  // 搜索 token
  // eslint-disable-next-line class-methods-use-this
  async searchTokens(keyword: string, board: string): Promise<any> {
    const { accessToken } = (await getAuth()) || {};
    if (!accessToken) {
      throw new Error('Access token not obtained, please log in first');
    }

    const headers = {
      Authorization: `Bearer ${accessToken}`,
      Accept: 'application/json',
    };

    try {
      if (board === 'USTOCK') {
        // 美股搜索
        const url = `${baseUrl}/hotSearching/search?keyword=${encodeURIComponent(keyword)}&isStock=true`;
        const response = await axios.get<any>(url, { headers });
        console.log('UStock search response:', response);
        return response.data ?? [];
        // return response;
      } else if (board === 'MEME') {
        // Meme 搜索
        const url = `${baseUrl}/hotSearching/search?keyword=${encodeURIComponent(keyword)}&isStock=false`;
        const response = await axios.get<any>(url, { headers });
        // return response.data?.data ?? [];
        console.log('Meme search response:', response);
        return response.data ?? [];
      } else if (board === 'CONTRACT') {
        // 合约/加密货币搜索
        const url = `${baseUrl}/hotSearching/searchPerpetual?keyword=${encodeURIComponent(keyword)}`;
        const response = await axios.get<any>(url, { headers });
        // return response.data?.data ?? [];
        console.log('UStock search response:', response);
        return response.data ?? [];
      }
      return [];
    } catch (error) {
      console.error('HotSearchDataProvider searchTokens error:', error);
      if (axios.isAxiosError(error)) {
        const errorMessage = error.response?.data?.message || error.message || 'Search error';
        throw new Error(errorMessage);
      }
      throw new Error('Failed to search tokens');
    }
  }
}

// 创建实例并导出
// @ts-ignore
export const hotSearchDataProvider = new HotSearchDataProvider();
